import { useLottie } from 'lottie-react'
import { animate, motion, useMotionValue, useTransform } from 'motion/react'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Caption1, Heading6, Title1 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'
import { Button } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { GiftIcon } from '@ui/icons/Gift.icon'

import { calculatePercentage } from '@utils/numbers.utils'

import {
  LoyaltyBonusStatus,
  LoyaltyDetails,
  LoyaltyTierDto,
} from '@model/IntroducingBrokerDetailsDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useMixedText } from '@hooks/useMixedText'

import ConfettiLottie from '@assets/lotties/Confetti/ConfettiV2.json'

import { ButtonShinyAnimated } from '@shared/components/ButtonShinyAnimated'
import { ProgressBarsBlock } from '@shared/components/ProgressBarsBlock/ProgressBarsBlock'

import { LoyaltyTierMapped } from '../../../Loyalty/Loyalty'

import styles from './BigLoyaltyCard.module.scss'

interface Props {
  loyaltyTiers?: LoyaltyDetails
  onSubmitReward: (tier?: LoyaltyTierDto | LoyaltyTierMapped | null) => Promise<void>
  isLoading: boolean
}

type TierNv = {
  currentNv: number
  requiredNv: number
}

const getNonSubtierNvForProgressBar = ({
  unClaimedApprovedTier,
  nextTier,
  currentTierNv,
  currentTierMinNv,
  currentTierMaxNv,
}: {
  unClaimedApprovedTier?: LoyaltyTierDto
  nextTier?: LoyaltyTierDto
  currentTierNv: number
  currentTierMinNv: number
  currentTierMaxNv: number
}): TierNv => {
  let currentNv: number
  let requiredNv: number

  if (unClaimedApprovedTier) {
    currentNv = unClaimedApprovedTier.bonusNv.current
    requiredNv = unClaimedApprovedTier.minNotionalValue
  } else if (!nextTier) {
    currentNv = currentTierNv
    requiredNv = currentTierMaxNv
  } else {
    currentNv = currentTierNv - currentTierMinNv
    requiredNv = currentTierMaxNv - currentTierMinNv
  }

  return { currentNv, requiredNv }
}

const formatLabelPercentage = (value: number): string => {
  return parseFloat(value.toFixed(2)).toString()
}

export const BigLoyaltyCard = ({ loyaltyTiers, onSubmitReward, isLoading }: Props) => {
  const { t } = useTranslation()
  const count = useMotionValue(0)
  const rounded = useTransform(() => formatLabelPercentage(count.get()))
  const { parseMixedText } = useMixedText()
  const { formatNumber, compactNumber } = useFormatNumber()
  const isArabic = useArabicSessionLanguage()

  const currentTier = loyaltyTiers?.currentTier
  const currentTierMinNv = currentTier?.minNotionalValue ?? 0
  const currentTierMaxNv = currentTier?.maxNotionalValue ?? 0
  const currentTierNv = currentTier?.bonusNv.current ?? 0
  const nextTier = loyaltyTiers?.nextTier

  const {
    View: Confetti,
    play: launchConfetti,
    stop: stopConfetti,
  } = useLottie({
    animationData: ConfettiLottie,
    autoplay: false,
    loop: false,
  })

  const unClaimedApprovedTier = useMemo(() => {
    return loyaltyTiers?.tiers?.find(
      (tier) => tier?.ibReward?.loyaltyBonusStatus.id === LoyaltyBonusStatus.Approved
    )
  }, [loyaltyTiers?.tiers])

  const unclaimedApprovedRewardId = unClaimedApprovedTier?.ibReward?.id
  const hasUnclaimedApprovedRewardId = !!unclaimedApprovedRewardId

  const heading = useMemo(
    () =>
      unClaimedApprovedTier || !nextTier
        ? t('IB.Dashboard.Current level')
        : t('IB.Dashboard.Next level'),
    [unClaimedApprovedTier, nextTier, t]
  )

  const notionalValuePercentage = useMemo(() => {
    // Use case: Bronze (4/4) is unclaimed, but sub-tiers are required to show slices of progress bar (4) instead of one. In this case look for sub-tiers level below, but only if current tier is unclaimed.
    const tierIndexWithSubTiersBeforeUnclaimedTier =
      loyaltyTiers?.tiers?.findIndex((tier, idx) => {
        const previousTier = loyaltyTiers?.tiers?.[idx - 1]

        return (
          !!tier.ibReward?.id &&
          !!unclaimedApprovedRewardId &&
          tier.ibReward?.id === unclaimedApprovedRewardId &&
          tier.bonusNv.current >= previousTier?.bonusNv.required &&
          (previousTier?.subTiers?.length ?? 0) > 0
        )
      }) ?? -1

    const tierWithSubTiersBeforeUnclaimed =
      tierIndexWithSubTiersBeforeUnclaimedTier > 0
        ? loyaltyTiers?.tiers?.[tierIndexWithSubTiersBeforeUnclaimedTier - 1]
        : undefined

    // WCA-4237 PR for extra info about workflow of this card.

    const subTiers =
      unClaimedApprovedTier?.subTiers ??
      currentTier?.subTiers ??
      tierWithSubTiersBeforeUnclaimed?.subTiers

    if (subTiers) {
      let ignoreRestCalculations = false

      return (
        subTiers?.map((tier, idx) => {
          const isUnclaimedApprovedTier =
            !!tier.ibReward?.id &&
            !!unclaimedApprovedRewardId &&
            tier.ibReward?.id === unclaimedApprovedRewardId

          // If unclaimed approved tier is found, then we should ignore rest calculations and just return 0's.
          // Essentially if 1/4 is filled and it's unclaimed, then skip calculations for 2/4, etc before moving to next tier.
          if (isUnclaimedApprovedTier) {
            ignoreRestCalculations = true
          }

          let currentNv
          let requiredNv

          if (unClaimedApprovedTier || !tier.isCurrent) {
            currentNv = tier.bonusNv.current
            requiredNv = tier?.bonusNv.required
          } else {
            currentNv = currentTierNv - currentTierMinNv
            requiredNv = currentTierMaxNv - currentTierMinNv
          }

          const percent = !ignoreRestCalculations ? calculatePercentage(currentNv, requiredNv) : 0

          // if isCurrent - no point to calculate anything else. Same goes to if current notional values are equal to required notional values. We know there is no necessary data in the next tiers.
          if (tier.isCurrent || tier.bonusNv.current === tier.bonusNv.required) {
            ignoreRestCalculations = true
          }

          return {
            percent,
            isCurrent: tier.isCurrent,
            isNextStarted:
              calculatePercentage(
                subTiers[idx + 1]?.bonusNv.current,
                subTiers[idx + 1]?.bonusNv.required
              ) > 0,
          }
        }) ?? []
      )
    }

    const skipPercentageCalc =
      currentTierNv === currentTierMinNv && !unClaimedApprovedTier && nextTier

    const { currentNv, requiredNv } = getNonSubtierNvForProgressBar({
      unClaimedApprovedTier,
      nextTier,
      currentTierNv,
      currentTierMinNv,
      currentTierMaxNv,
    })

    return [
      {
        percent: skipPercentageCalc ? 0 : calculatePercentage(currentNv, requiredNv),
        isCurrent: true,
      },
    ]
  }, [
    unClaimedApprovedTier,
    currentTier,
    loyaltyTiers?.tiers,
    currentTierNv,
    currentTierMinNv,
    currentTierMaxNv,
    nextTier,
  ])

  useEffect(() => {
    // We show 0 if amount of current notional value is equal to next tier minimum notional value (not applicable for unclaimed approved tier)
    const shouldSkipPercentageCalc =
      currentTierNv === currentTierMinNv && !unClaimedApprovedTier && nextTier

    const { currentNv, requiredNv } = getNonSubtierNvForProgressBar({
      unClaimedApprovedTier,
      nextTier,
      currentTierNv,
      currentTierMinNv,
      currentTierMaxNv,
    })

    const controls = animate(
      count,
      shouldSkipPercentageCalc ? 0 : calculatePercentage(currentNv, requiredNv),
      {
        duration: 2,
      }
    )
    return () => controls.stop()
  }, [count, unClaimedApprovedTier, currentTierNv, currentTierMinNv, currentTierMaxNv, nextTier])

  const handleConfetti = () => {
    stopConfetti()
    launchConfetti()
  }

  useEffect(() => {
    if (!unClaimedApprovedTier) {
      const timeout = setTimeout(() => {
        stopConfetti()
      }, 2000)

      return () => clearTimeout(timeout)
    }

    handleConfetti()

    const interval = setInterval(() => {
      handleConfetti()
    }, 3000)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unClaimedApprovedTier])

  const tierImgUri = unClaimedApprovedTier?.imageUri ?? nextTier?.imageUri ?? currentTier?.imageUri
  const tierLabel = unClaimedApprovedTier?.label ?? nextTier?.label ?? currentTier?.label

  const barCurrentNvLabelValue = currentTierNv
  const barCurrentNvLabel =
    barCurrentNvLabelValue >= 0 ? `${compactNumber(barCurrentNvLabelValue, 4)} ` : ''
  const barRequiredNvLabelValue = unClaimedApprovedTier
    ? unClaimedApprovedTier?.minNotionalValue
    : nextTier?.minNotionalValue ?? currentTier?.minNotionalValue
  const barRequiredNvLabel = compactNumber(barRequiredNvLabelValue ?? 0, 4)

  return (
    <article className={styles.bigLoyaltyCard}>
      <div className={styles.confetti}>{Confetti}</div>
      <header className={styles.topSection}>
        {tierImgUri && (
          <img
            src={tierImgUri}
            alt={tierLabel || 'loyalty tier icon'}
            width={48}
            height={48}
            loading='lazy'
          />
        )}

        <div className={styles.textBox}>
          <Title1>{heading}</Title1>
          <div className={styles.title}>
            <Heading6 medium>
              {unClaimedApprovedTier
                ? unClaimedApprovedTier?.label ?? ''
                : nextTier?.label ?? currentTier?.label ?? ''}
            </Heading6>
            <Badge type='black' contrast='low' size='xs'>
              {isArabic && parseMixedText('%')}
              <motion.span>{rounded}</motion.span>
              {!isArabic && parseMixedText('%')}
            </Badge>
          </div>
        </div>
      </header>
      <section className={styles.bottomSection}>
        <ProgressBarsBlock
          items={[
            {
              title: (
                <Caption1 className={styles.notionalValueLabel}>{t('IB.Notional Value')}</Caption1>
              ),
              value: (
                <Caption1 className={styles.notionalValueLabel}>
                  <SensitiveData>{`${barCurrentNvLabel}`}</SensitiveData>
                  {`/ ${barRequiredNvLabel}`}
                </Caption1>
              ),
              sections: notionalValuePercentage,
              progressTooltip: formatNumber(currentTier?.bonusNv?.current ?? 0, {
                minDecimalPlaces: 0,
              }),
            },
          ]}
          color='red'
          size='small'
          compact
          useBodyTypography={false}
        />

        <Tooltip
          content={t('IB.Check eligibility criteria')}
          disabled={hasUnclaimedApprovedRewardId || isLoading}
          wrapperClassName={styles.claimRewardTooltip}
        >
          <ButtonShinyAnimated fullWidth animated={!!unClaimedApprovedTier?.ibReward?.id}>
            <Button.Primary
              size='lg'
              fullWidth
              iconLeft={{
                icon: GiftIcon,
                color: hasUnclaimedApprovedRewardId ? 'text-white' : 'text-primary-base-em',
              }}
              disabled={!hasUnclaimedApprovedRewardId || isLoading}
              onClick={() => {
                onSubmitReward(unClaimedApprovedTier)
              }}
            >
              {t('IB.Dashboard.Claim Rewards')}
            </Button.Primary>
          </ButtonShinyAnimated>
        </Tooltip>
      </section>
    </article>
  )
}
